<script setup>
import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
import LandingPageLayout from '@/Layouts/LandingPageLayout.vue'
import { XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'

import moment from 'moment'
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    Switch,
    SwitchGroup,
    SwitchLabel,
} from '@headlessui/vue'

import { reactive, ref, watch, onMounted } from 'vue'
import InputError from '@/Components/InputError.vue'
import Pagination from '@/Components/Shared/Pagination.vue'
import InputLabel from '@/Components/InputLabel.vue'
import TextInput from '@/Components/TextInput.vue'

import Modal from '@/Components/Modal.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import SingleListing from '@/Pages/Listings/SingleListing.vue'

const props = defineProps({
    tails: Object,
    sendAccessURL: String,
    requestMoreInfoURL: String,
    lastrequestemail: Object,
    lastgrantemail: Object,
    first: String,
    last: String,
    email: String,
    owner: Object,
    filters: Object,
    singleTail: String,
})

const sortOptions = reactive({
    Price: 'Price',
})

const typeOptions = reactive({
    Piston: 'Piston',
    Turboprop: 'Turboprop',
    Jet: 'Jet',
})

const numEngineOptions = reactive({
    Single: 'Single',
    Twin: 'Twin',
})

const listings = useForm({
    search: props.filters.search,
    sortBy: props.filters.sortBy,
    sortDirection: props.filters.sortDirection,
    type: props.filters.type,
    numEng: props.filters.numEng,
    hasPhotos: props.filters.hasPhotos === 'true',
})

const resetSearchFilters = () => {
    listings.search = null
    listings.sortBy = ''
    listings.sortDirection = ''
    listings.type = null
    listings.numEng = null
    listings.hasPhotos = false
}

const search = () => {
    return debounce(() => {
        listings.get(`/listings`, pickBy(form), {
            preserveState: false,
        })
    }, 400)
}

watch(
    () => [
        listings.search,
        listings.sortBy,
        listings.sortDirection,
        listings.type,
        listings.numEng,
        listings.hasPhotos,
    ],
    search(),
    { deep: true }
)

const form = useForm({
    tail_id: '',
    first: '',
    last: '',
    email: '',
    illegible: '',
})

const showRequestForm = ref(false)

const closeForm = () => {
    form.reset()
    showRequestForm.value = false
}

const wantsAccess = (data) => {
    form.tail_id = data.tail.id
    showRequestForm.value = true
}

const requestAccess = (tail) => {
    form.post(route('listing-request'), {
        preserveState: true,
        onError: (errors) => {
            console.log('errors', errors)
        },
        onSuccess: () => {
            console.log('yay')
            closeForm()
            closeSingleListing()
        },
    })
}

const handleAccess = (grant = false) => {
    form.tail_id = props.tails.data[0].id
    form.first = props.first
    form.last = props.last
    form.email = props.email
    let url = props.requestMoreInfoURL
    if (grant) {
        url = props.sendAccessURL
    }
    form.post(url, {
        preserveState: true,
        onError: (errors) => {
            console.log('errors', errors)
        },
        onSuccess: () => {
            console.log('yay')
        },
    })
}

onMounted(() => {
    if (props.singleTail !== '' && props.singleTail) {
        listings.search = props.singleTail
    }
})

const showSingleListing = ref(false)
const singleListing = reactive({})

const showSingle = (data) => {
    showSingleListing.value = true
    singleListing.value = data.tail
    //sharingLink.value = 'https://planelogix.com/listings/'+tail.TailNumber;
}

const closeSingleListing = () => {
    showSingleListing.value = false
    singleListing.value = {}
    //sharingLink.value = '';
}
</script>
<template>
    <LandingPageLayout title="Listings">
        <div class="isolate overflow-hidden">
            <div class="flow-root bg-gray-900 py-16 sm:pt-32 lg:pb-30">
                <div class="mx-auto max-w-7xl px-6 lg:px-8">
                    <div class="relative z-10">
                        <h1 class="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight text-white">
                            Listings
                        </h1>
                        <p class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60">
                            View aircraft that are on our platform and up for sale by their owners
                        </p>
                        <p class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60">
                            Do your pre-buy research like never before...
                        </p>
                    </div>
                    <div
                        class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-2"
                    >
                        <svg
                            viewBox="0 0 1208 1024"
                            aria-hidden="true"
                            class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0"
                        >
                            <ellipse
                                cx="604"
                                cy="512"
                                fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)"
                                rx="604"
                                ry="512"
                            />
                            <defs>
                                <radialGradient id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867">
                                    <stop stop-color="#111827" />
                                    <stop offset="1" stop-color="#01b2ff" />
                                </radialGradient>
                            </defs>
                        </svg>
                        <div
                            class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Digital only or Digital Hardcopy -->
        <div class="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-16 lg:max-w-7xl lg:px-8">
            <!-- Details section -->
            <section aria-labelledby="details-heading">
                <!--<div class="flex flex-col items-center text-center hidden">
                    <h2 id="details-heading" class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">LISTINGS</h2>
                    <p class="mt-3 max-w-3xl text-lg text-gray-600">View aircraft that are on our platform and up for sale by their owners</p>

                    <p class="mt-3 max-w-3xl text-lg text-gray-600">Do your pre-buy research like never before...</p>

                </div>-->
                <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-1">
                    <div v-if="props.sendAccessURL && props.requestMoreInfoURL">
                        <div class="px-4 sm:px-0">
                            <h3 class="text-base font-semibold leading-7 text-gray-900">
                                Access Requester Information
                            </h3>
                        </div>
                        <div class="mt-6 border-t border-gray-100">
                            <dl class="divide-y divide-gray-100">
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <dt class="text-sm font-medium leading-6 text-gray-900">Full name</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{ props.first }} {{ props.last }}
                                    </dd>
                                </div>
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <dt class="text-sm font-medium leading-6 text-gray-900">Email address</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{ props.email }}
                                    </dd>
                                </div>
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <dt class="text-sm font-medium leading-6 text-gray-900">Access Last Sent</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{
                                            props.lastgrantemail
                                                ? moment(props.lastgrantemail).format('YYYY-MM-DD  hh:mm:ss a')
                                                : 'Never'
                                        }}
                                    </dd>
                                </div>
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <dt class="text-sm font-medium leading-6 text-gray-900">
                                        More Info Request Last Sent
                                    </dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{
                                            props.lastrequestemail
                                                ? moment(props.lastrequestemail).format('YYYY-MM-DD  hh:mm:ss a')
                                                : 'Never'
                                        }}
                                    </dd>
                                </div>
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <dt class="text-sm font-medium leading-6 text-gray-900">Available Actions</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        <button
                                            type="button"
                                            class="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                                            @click="handleAccess(true)"
                                        >
                                            Grant Access
                                        </button>
                                        <button
                                            type="button"
                                            class="ml-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                            @click="handleAccess(false)"
                                        >
                                            Request More Info
                                        </button>
                                    </dd>
                                </div>
                                <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                    <h3 class="text-base font-semibold leading-7 text-gray-900">
                                        Aircraft Owner and Email
                                    </h3>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{ props.owner.FullName }}, {{ props.owner.email }}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
                <!--Search-->
                <div class="mx-5 flex grid lg:grid-cols-3 lg:gap-3 sm:grid-cols-2 overflow-visible">
                    <div class="sm:flex-auto">
                        <InputLabel for="text" value="Search Filter" />
                        <TextInput
                            id="text"
                            v-model="listings.search"
                            type="text"
                            class="mt-1 block w-full"
                            autofocus
                        />
                        <InputError class="mt-2" :message="listings.errors.search" />
                        <SwitchGroup as="div" class="flex items-center sm:mt-5 mt-2 mb-2">
                            <Switch
                                v-model="listings.hasPhotos"
                                :class="[
                                    listings.hasPhotos ? 'bg-indigo-600' : 'bg-gray-200',
                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
                                ]"
                            >
                                <span class="sr-only">Has Photos</span>
                                <span
                                    aria-hidden="true"
                                    :class="[
                                        listings.hasPhotos ? 'translate-x-5' : 'translate-x-0',
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                    ]"
                                />
                            </Switch>
                            <SwitchLabel as="span" class="ml-3 text-sm">
                                <span class="font-medium text-gray-900">Has Photos</span>
                            </SwitchLabel>
                        </SwitchGroup>
                    </div>

                    <div class="sm:flex-auto">
                        <div class="sm:flex-auto grid grid-cols-2">
                            <div>
                                <InputLabel for="text" value="Sort By" />
                                <select
                                    v-model="listings.sortBy"
                                    class="mt-1 block w-full rounded-md border-0 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                >
                                    <option v-for="(label, key) in sortOptions" :value="key">{{ label }}</option>
                                </select>
                            </div>
                            <div>
                                <InputLabel for="text" value="Sort Direction" />
                                <select
                                    v-model="listings.sortDirection"
                                    class="mt-1 block w-full rounded-md border-0 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                >
                                    <option value="asc">Asc</option>
                                    <option value="desc">Desc</option>
                                </select>
                            </div>
                        </div>
                        <div class="sm:flex-auto grid grid-cols-2">
                            <div>
                                <InputLabel for="text" value="Type" />
                                <select
                                    v-model="listings.type"
                                    class="mt-1 block w-full rounded-md border-0 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                >
                                    <option v-for="(label, key) in typeOptions" :value="key">{{ label }}</option>
                                </select>
                            </div>
                            <div>
                                <InputLabel for="text" value="Engine Count" />
                                <select
                                    v-model="listings.numEng"
                                    class="mt-1 block w-full rounded-md border-0 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                >
                                    <option v-for="(label, key) in numEngineOptions" :value="key">{{ label }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="grid grid-cols-2">
                        <div
                            class="sm:flex-auto justify-items-end grid inline-block align-middle mt-5 h-1 m-auto mb-10"
                        >
                            <primary-button
                                color="logix-light-blue"
                                :disabled="listings.processing"
                                @click="resetSearchFilters"
                                >Reset All Filters</primary-button
                            >
                        </div>
                    </div>
                </div>
                <!--Listings-->
                <div
                    class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3"
                >
                    <SingleListing :tails="tails.data" :processing="form.processing" @show="showSingle"></SingleListing>
                </div>
                <div v-if="tails.data.length === 0">
                    <div class="px-4 py-5">
                        <div class="mt-2 sm:flex sm:items-start sm:justify-between">
                            <div class="max-w-xl text-sm text-gray-500">
                                <p>No results found</p>
                            </div>
                            <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex sm:flex-shrink-0"></div>
                        </div>
                    </div>
                </div>

                <div class="mt-5">
                    <pagination
                        class="mt-6"
                        :links="tails.meta.links"
                        :meta="tails.meta"
                        :top-links="tails.links"
                        :show-go-to="true"
                    />
                </div>

                <!--SHOW A SINGLE LISTING IN LARGE MODAL-->
                <Modal :show="showSingleListing && !showRequestForm" closeable z-index="z-10" max-width="4xl" @close="closeSingleListing">
                    <div class="px-6 py-4 flex items-start justify-between">
                        <SingleListing
                            :single-mode="true"
                            :tails="singleListing"
                            :processing="form.processing"
                            @request="wantsAccess"
                        ></SingleListing>
                        <!--<div class="ml-4 flex flex-shrink-0 ">
                            <button type="button" @click="closeSingleListing" class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                <span class="sr-only">Close</span>
                                <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>-->
                    </div>
                    <div class="flex flex-row justify-end px-6 py-4 bg-gray-100 text-right">
                        <SecondaryButton @click="closeSingleListing">Close</SecondaryButton>
                    </div>
                </Modal>

                <!--REQUEST ACESS MODAL FORM-->
                <TransitionRoot as="template" :show="showRequestForm">
                    <Dialog as="div" class="relative z-20" @close="showRequestForm = false">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-20 overflow-y-auto">
                            <div
                                class="flex sm:min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                            >
                                <TransitionChild
                                    as="template"
                                    enter="ease-out duration-300"
                                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <DialogPanel
                                        class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
                                    >
                                        <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                                            <button
                                                type="button"
                                                class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                                @click="showRequestForm = false"
                                            >
                                                <span class="sr-only">Close</span>
                                                <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <div class="sm:flex sm:items-start">
                                            <div
                                                class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10"
                                            >
                                                <QuestionMarkCircleIcon
                                                    class="h-6 w-6 text-blue-600"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <DialogTitle
                                                    as="h3"
                                                    class="text-base font-semibold leading-6 text-gray-900"
                                                    >Access Request Form</DialogTitle
                                                >
                                                <div class="mt-2">
                                                    <p class="text-sm text-gray-500">
                                                        To request access, please provide us with your name and email.
                                                    </p>
                                                </div>
                                                <div class="isolate -space-y-px rounded-md shadow-sm">
                                                    <div
                                                        class="relative rounded-md rounded-b-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-600"
                                                    >
                                                        <label
                                                            for="first"
                                                            class="block text-xs font-medium text-gray-900"
                                                            >First Name</label
                                                        >
                                                        <input
                                                            id="first"
                                                            v-model="form.first"
                                                            type="text"
                                                            name="first"
                                                            class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                            placeholder="Jane"
                                                        />
                                                        <InputError class="mt-2" :message="form.errors.first" />
                                                    </div>
                                                    <div
                                                        class="relative rounded-t-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-600"
                                                    >
                                                        <label
                                                            for="last"
                                                            class="block text-xs font-medium text-gray-900"
                                                            >Last Name</label
                                                        >
                                                        <input
                                                            id="last"
                                                            v-model="form.last"
                                                            type="text"
                                                            name="last"
                                                            class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                            placeholder="Smith"
                                                        />
                                                        <InputError class="mt-2" :message="form.errors.last" />
                                                    </div>
                                                    <div
                                                        class="relative rounded-md rounded-t-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-600"
                                                    >
                                                        <label
                                                            for="email"
                                                            class="block text-xs font-medium text-gray-900"
                                                            >Email</label
                                                        >
                                                        <input
                                                            id="email"
                                                            v-model="form.email"
                                                            type="email"
                                                            name="email"
                                                            class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                                            placeholder="you@example.com"
                                                        />
                                                        <InputError class="mt-2" :message="form.errors.email" />
                                                    </div>
                                                </div>
                                                <InputError class="mt-2" :message="form.errors.illegible" />
                                            </div>
                                        </div>
                                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                class="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                                                @click.prevent="requestAccess"
                                            >
                                                Request
                                            </button>
                                            <button
                                                type="button"
                                                class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                @click="showRequestForm = false"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </DialogPanel>
                                </TransitionChild>
                            </div>
                        </div>
                    </Dialog>
                </TransitionRoot>
            </section>
        </div>
    </LandingPageLayout>
</template>
